/**
 * Admin org detail — full OrgDetail view plus license lifecycle actions (suspend / revoke / renew).
 * Each action requires a two-tap confirm (tap once to arm, tap "Confirm" to execute), then refetches.
 * Actions are disabled when there is no license (licenseId === null).
 */
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgDetail } from '@beam/api-types';
import { adminGetOrg, adminLicenseAction, AdminApiError } from '../../../src/api/adminClient';
import { Screen, Text, Card, Badge, Button } from '../../../src/components/ui';
import { licenseBadge } from '../../../src/components/ui';
import { AdminHeader } from '../../../src/components/admin/AdminHeader';
import { theme } from '../../../src/theme';

type LicenseAction = 'suspend' | 'revoke' | 'renew';

function DetailRow({ label, value }: { label: string; value: string | number | null }) {
  return (
    <View style={styles.detailRow}>
      <Text variant="small" color="secondary" style={styles.detailLabel}>
        {label}
      </Text>
      <Text variant="data" selectable style={styles.detailValue}>
        {value == null || value === '' ? '—' : String(value)}
      </Text>
    </View>
  );
}

function OrgDetailBody({ org }: { org: OrgDetail }) {
  const queryClient = useQueryClient();
  const [armed, setArmed] = useState<LicenseAction | null>(null);
  const [busy, setBusy] = useState<LicenseAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);

  const hasLicense = org.licenseId != null;
  const statusSpec = org.status ? licenseBadge(org.status) : null;

  async function runAction(action: LicenseAction) {
    if (!org.licenseId) return;
    if (armed !== action) {
      setArmed(action);
      setActionError(null);
      setActionResult(null);
      return;
    }
    setBusy(action);
    setActionError(null);
    setActionResult(null);
    try {
      const res = await adminLicenseAction(org.licenseId, action);
      setActionResult(`License ${action} succeeded — status is now "${res.status}".`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'org', org.organizationId] });
    } catch (err) {
      setActionError(err instanceof AdminApiError || err instanceof Error ? err.message : `Failed to ${action} license.`);
    } finally {
      setBusy(null);
      setArmed(null);
    }
  }

  const actions: { action: LicenseAction; title: string; variant: 'secondary' | 'danger' }[] = [
    { action: 'suspend', title: 'Suspend', variant: 'secondary' },
    { action: 'revoke', title: 'Revoke', variant: 'danger' },
    { action: 'renew', title: 'Renew', variant: 'secondary' },
  ];

  return (
    <>
      <Card>
        <View style={styles.head}>
          <Text variant="title" numberOfLines={2}>
            {org.legalName}
          </Text>
          {statusSpec ? <Badge status={statusSpec.key} label={statusSpec.label} /> : null}
          {org.isComped ? <Badge status="sent" label="Comped" /> : null}
        </View>
        <DetailRow label="Organization ID" value={org.organizationId} />
        <DetailRow label="Owner email" value={org.ownerEmail} />
        <DetailRow label="Users" value={org.userCount} />
        <DetailRow label="Plan" value={org.plan} />
        <DetailRow label="Seats" value={org.seats} />
        <DetailRow label="Current period end" value={org.currentPeriodEnd} />
        <DetailRow label="Created" value={org.createdAt} />
      </Card>

      <Card>
        <Text variant="label" color="secondary">
          License &amp; billing
        </Text>
        <DetailRow label="License ID" value={org.licenseId} />
        <DetailRow label="Scope value" value={org.scopeValue} />
        <DetailRow label="Stripe subscription" value={org.stripeSubscriptionId} />
        <DetailRow label="Stripe customer" value={org.stripeCustomerId} />
        <DetailRow label="Notes" value={org.notes} />
      </Card>

      <Card>
        <Text variant="label" color="secondary">
          License actions
        </Text>
        {!hasLicense ? (
          <Text variant="small" color="secondary">
            This organization has no license — actions are unavailable.
          </Text>
        ) : (
          <Text variant="small" color="secondary">
            Tap an action to arm it, then tap Confirm to apply.
          </Text>
        )}
        <View style={styles.actions}>
          {actions.map(({ action, title, variant }) => (
            <Button
              key={action}
              title={armed === action ? `Confirm ${title.toLowerCase()}` : title}
              size="md"
              variant={armed === action ? 'accent' : variant}
              fullWidth={false}
              disabled={!hasLicense || (busy !== null && busy !== action)}
              loading={busy === action}
              onPress={() => void runAction(action)}
            />
          ))}
          {armed ? (
            <Button title="Cancel" size="md" variant="ghost" fullWidth={false} onPress={() => setArmed(null)} />
          ) : null}
        </View>
        {actionError ? <Text color="danger">{actionError}</Text> : null}
        {actionResult ? (
          <Text variant="small" color="link">
            {actionResult}
          </Text>
        ) : null}
      </Card>
    </>
  );
}

export default function AdminOrgDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'org', id],
    queryFn: () => adminGetOrg(id),
    enabled: !!id,
  });

  return (
    <Screen scroll>
      <AdminHeader active="orgs" />

      <Text variant="small" color="link" onPress={() => router.replace('/admin/orgs')} accessibilityRole="button" style={styles.back}>
        ← Back to organizations
      </Text>

      {isLoading ? (
        <Card>
          <Text color="secondary">Loading organization…</Text>
        </Card>
      ) : error ? (
        <Card>
          <Text color="danger">{error instanceof AdminApiError || error instanceof Error ? error.message : 'Failed to load organization.'}</Text>
        </Card>
      ) : data ? (
        <OrgDetailBody org={data} />
      ) : (
        <Card>
          <Text color="secondary">Organization not found.</Text>
        </Card>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  back: { paddingVertical: theme.spacing.xs },
  head: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md, flexWrap: 'wrap' },
  detailRow: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.md, flexWrap: 'wrap' },
  detailLabel: { width: 160 },
  detailValue: { flex: 1, minWidth: 200 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap' },
});
